import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, resolveImage } from "@/lib/api";
import { formatMoney, currencySymbol } from "@/lib/format";
import { PageLoader, EmptyState, ErrorState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Eye, Heart, Pencil, Tag, CheckCircle2, Archive, RotateCcw, MessageSquareText } from "lucide-react";

function Row({ l, children }) {
  return (
    <div className="flex gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`my-listing-${l.id}`}>
      <Link to={`/listing/${l.id}`} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-secondary">
        {l.images?.[0] && <img src={resolveImage(l.images[0])} alt="" className="h-full w-full object-cover" />}
      </Link>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{l.title}</p>
        <p className="text-sm font-bold text-price">{formatMoney(l.price, l.currency)}</p>
        <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{l.views_count || 0}</span>
          <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{l.favorites_count || 0}</span>
          {l.sold && <span className="font-semibold text-brand">SOLD</span>}
          {l.relist_pending && <span className="font-semibold text-amber-600">Relist pending</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">{children}</div>
      </div>
    </div>
  );
}

const btn = "flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-secondary";

export default function MyListings() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState("live");
  const [reduceFor, setReduceFor] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [relistFor, setRelistFor] = useState(null);
  const [relistReason, setRelistReason] = useState("");

  const { data: listings, isLoading, isError, refetch } = useQuery({ queryKey: ["my-listings"], queryFn: () => apiFetch("/my/listings") });
  const { data: archived } = useQuery({ queryKey: ["my-archived"], queryFn: () => apiFetch("/my/archived") });

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["my-listings"] }); qc.invalidateQueries({ queryKey: ["my-archived"] }); }
    catch (err) { toast.error(err.message); }
  };

  const submitReduce = async () => {
    await act(() => apiFetch(`/listings/${reduceFor.id}/reduce-price?price=${Number(newPrice)}`, { method: "POST" }), "Price reduced");
    setReduceFor(null); setNewPrice("");
  };
  const submitRelist = async () => {
    await act(() => apiFetch(`/listings/${relistFor.id}/relist-request`, { method: "POST", body: JSON.stringify({ reason: relistReason.trim() }) }), "Relist request sent");
    setRelistFor(null); setRelistReason("");
  };

  if (isLoading) return <PageLoader />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const live = listings.filter((l) => !l.sold);
  const sold = listings.filter((l) => l.sold);
  const tabs = [
    { k: "live", label: `Live (${live.length})` },
    { k: "sold", label: `Sold (${sold.length})` },
    { k: "archived", label: `Archived (${archived?.length || 0})` },
  ];
  const rows = tab === "live" ? live : tab === "sold" ? sold : (archived || []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold">My Listings</h1>
        <button onClick={() => navigate("/create")} className="rounded-full bg-price px-4 py-2 text-sm font-semibold text-white">+ New</button>
      </div>

      <div className="mt-4 flex gap-2 border-b border-border">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === t.k ? "border-price text-price" : "border-transparent text-muted-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {rows.length === 0 ? (
          <EmptyState icon={Package} title="Nothing here yet" message={tab === "live" ? "Publish your first item to start selling." : "No items in this tab."} action={tab === "live" ? <button onClick={() => navigate("/create")} className="mt-2 rounded-full bg-price px-4 py-2 text-sm font-semibold text-white">Sell an item</button> : null} />
        ) : (
          rows.map((l) => (
            <Row key={l.id} l={l}>
              {tab === "archived" ? (
                <button className={btn} onClick={() => act(() => apiFetch(`/listings/${l.id}/restore`, { method: "POST" }), "Restored")} data-testid={`restore-${l.id}`}><RotateCcw className="h-3 w-3" />Restore</button>
              ) : tab === "sold" ? (
                <>
                  <button className={btn} onClick={() => navigate(`/offers/${l.id}`)}><MessageSquareText className="h-3 w-3" />Offers</button>
                  {!l.relist_pending && <button className={btn} onClick={() => setRelistFor(l)} data-testid={`relist-${l.id}`}><RotateCcw className="h-3 w-3" />Relist</button>}
                </>
              ) : (
                <>
                  <button className={btn} onClick={() => navigate(`/edit/${l.id}`)}><Pencil className="h-3 w-3" />Edit</button>
                  <button className={btn} onClick={() => setReduceFor(l)} data-testid={`reduce-${l.id}`}><Tag className="h-3 w-3" />Reduce</button>
                  <button className={btn} onClick={() => act(() => apiFetch(`/listings/${l.id}/sold?sold=true`, { method: "POST" }), "Marked as sold")} data-testid={`sold-${l.id}`}><CheckCircle2 className="h-3 w-3" />Sold</button>
                  <button className={btn} onClick={() => navigate(`/offers/${l.id}`)}><MessageSquareText className="h-3 w-3" />Offers</button>
                  <button className={btn} onClick={() => act(() => apiFetch(`/listings/${l.id}/archive`, { method: "POST" }), "Archived")} data-testid={`archive-${l.id}`}><Archive className="h-3 w-3" />Archive</button>
                </>
              )}
            </Row>
          ))
        )}
      </div>

      <Dialog open={!!reduceFor} onOpenChange={(o) => !o && setReduceFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reduce price</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Current: {reduceFor && formatMoney(reduceFor.price, reduceFor.currency)}. New price must be lower.</p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{reduceFor && currencySymbol(reduceFor.currency)}</span>
            <input data-testid="reduce-price-input" type="number" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className="w-full rounded-xl border border-input py-2.5 pl-8 pr-4 text-sm outline-none focus:border-price" />
          </div>
          <DialogFooter><button onClick={submitReduce} className="rounded-xl bg-price px-5 py-2.5 text-sm font-semibold text-white" data-testid="submit-reduce">Reduce</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!relistFor} onOpenChange={(o) => !o && setRelistFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request relist</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Relisting a sold item needs admin approval. Tell us why.</p>
          <textarea data-testid="relist-reason" value={relistReason} onChange={(e) => setRelistReason(e.target.value)} rows={3} className="w-full rounded-xl border border-input p-3 text-sm outline-none focus:border-price" />
          <DialogFooter><button onClick={submitRelist} disabled={relistReason.trim().length < 3} className="rounded-xl bg-price px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="submit-relist">Send request</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
